import React, { useState } from 'react';
import { CaptionProps, DateRange, DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { subMonths, addMonths, subYears, addYears } from 'date-fns';
import { COLOR_MAP } from '../../constants/ColorNames';
import Caption from './DateRangeComponents/Caption';
import CustomHeader from './DateRangeComponents/Header';
import Footer from './DateRangeComponents/Footer';

type DateRangeProps = {
  isOpen: boolean;
  handleDateRangeApply: (range: any) => void;
};

const css = `
  .rdp {
    padding: 100px 0 0 0;
  }
  .my-selected:not([disabled]) { 
    font-weight: bold; 
    border: 2px solid currentColor;
  }
  .my-selected:hover:not([disabled]) { 
    border-color: blue;
    color: blue;
  }
  .my-today { 
    font-weight: bold;
    font-size: 140%; 
    color: red;
  }
  .rdp-day {
    width: 42px;
    height: 42px;
    max-width: 42px;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${COLOR_MAP.GREY_3}
  }
  .rdp-cell{
    padding: 2px;
  }
  .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
    border-radius: 4px !important;
    background: ${COLOR_MAP.DISABLED}
  }
  .rdp-day_selected, .rdp-day_selected:focus-visible, .rdp-day_selected:hover{
    background: ${COLOR_MAP.DARK_PURPLE}
  }
  .rdp-day_selected{
    border-radius: 4px !important;
    color: ${COLOR_MAP.WHITE} !important;
    background: ${COLOR_MAP.DARK_PURPLE}
  }

  .DayPicker-Day:hover {
    border-radius: 4px; /* Remove the rounded border */
    background: lightblue; /* Change the hover background color */
  }

`;

const DateRangePicker = (props: DateRangeProps) => {
  const [range, setRange] = useState<DateRange | undefined>();
  const { isOpen, handleDateRangeApply } = props;
  const currentMonth = addMonths(new Date(), 0);
  const [month, setMonth] = useState<Date>(currentMonth);

  const handleRangeChange = React.useCallback((newRange: DateRange | undefined) => {
    setRange(newRange);
  },[]);

  const navigateMonth = React.useCallback((action: string) => {
    switch (action) {
      case 'prev':
        setMonth(subMonths(month, 1));
        break;
      case 'next':
        setMonth(addMonths(month, 1));
        break;
      case 'prevYear':
        setMonth(subYears(month, 1));
        break;
      case 'nextYear':
        setMonth(addYears(month, 1));
        break;
      default:
        break;
    }    
  },[month])

  const getCustomHeader = () => {
    return (
      <CustomHeader range={range}  handleRangeChange={handleRangeChange} />
    )
  }

  const getCaption = (props: CaptionProps) => {
    return (
      <Caption displayMonth={props.displayMonth} navigateMonth={navigateMonth}/>
    )
  }

  if (!isOpen) return <></>;

  return (
    <>
      <style>{css}</style>
      <DayPicker
        mode="range"
        selected={range}
        month={month}
        footer={<Footer
          range={range}
          handleDateRangeApply={handleDateRangeApply}
          handleRangeChange={handleRangeChange}
        />}
        components={{ Head: getCustomHeader, Caption: getCaption }}
        onSelect={handleRangeChange}
        showOutsideDays={true}
      />
    </>
  );
};

export default DateRangePicker;
